import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Heart, ArrowUpRight, Loader2, Clock, Scissors } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function StyleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [style, setStyle] = useState(null);
  const [favorited, setFavorited] = useState(false);
  const [tips, setTips] = useState("");
  const [loadingTips, setLoadingTips] = useState(false);

  useEffect(() => {
    api.get(`/hairstyles/${id}`).then((r) => setStyle(r.data));
    if (user) {
      api.get("/favorites").then((r) => {
        setFavorited(!!(r.data.favorites || []).find((f) => f.style_id === id));
      });
    }
  }, [id, user]);

  const toggleFav = async () => {
    if (!user) {
      toast.error("Sign in to save favorites.");
      return;
    }
    try {
      if (favorited) {
        await api.delete(`/favorites/${id}`);
        setFavorited(false);
        toast.success("Removed from favorites");
      } else {
        await api.post(`/favorites/${id}`);
        setFavorited(true);
        toast.success("Saved to favorites");
      }
    } catch (_e) {
      toast.error("Action failed");
    }
  };

  const loadTips = async () => {
    setLoadingTips(true);
    try {
      const r = await api.post("/tips", { style_id: id });
      setTips(r.data.tips);
    } catch (_e) {
      toast.error("Could not load tips");
    } finally {
      setLoadingTips(false);
    }
  };

  if (!style) {
    return <main className="crown-container py-20 text-center font-overline" data-testid="style-loading">Loading…</main>;
  }

  return (
    <main className="crown-container py-12 md:py-16" data-testid="style-detail-page">
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Image */}
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={style.image_url} alt={style.name} className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5 lg:pt-8">
          <p className="font-overline mb-3">{style.gender} · {style.category}</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-6">{style.name}</h1>
          <p className="text-ash leading-relaxed mb-8">{style.description}</p>

          <div className="flex flex-wrap gap-2 mb-10">
            {style.tags.map((t) => (
              <span key={t} className="font-overline text-[0.6rem] border border-bone/20 px-3 py-1.5 text-ash">{t}</span>
            ))}
          </div>

          <div className="space-y-5 mb-10">
            <div>
              <p className="font-overline text-ash mb-1 flex items-center gap-2"><Clock size={12} strokeWidth={1.5} /> Duration</p>
              <p className="font-serif text-xl">{style.duration}</p>
            </div>
            <div>
              <p className="font-overline text-ash mb-1 flex items-center gap-2"><Scissors size={12} strokeWidth={1.5} /> Stylist instructions</p>
              <p className="text-bone leading-relaxed">{style.stylist_instructions}</p>
            </div>
            <div>
              <p className="font-overline text-ash mb-1">Maintenance</p>
              <p className="text-bone leading-relaxed">{style.maintenance}</p>
            </div>
            <div>
              <p className="font-overline text-ash mb-1">Best for face shapes</p>
              <p className="capitalize text-bone">{style.suitable_face_shapes.join(" · ")}</p>
            </div>
            <div>
              <p className="font-overline text-ash mb-1">Best for hair types</p>
              <p className="capitalize text-bone">{style.suitable_hair_types.join(" · ")}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            <button onClick={toggleFav} className={favorited ? "crown-btn-primary" : "crown-btn-ghost"} data-testid="favorite-toggle-btn">
              <Heart size={14} fill={favorited ? "currentColor" : "none"} strokeWidth={1.5} />
              {favorited ? "Saved" : "Save to favorites"}
            </button>
            <Link to="/analyze" className="crown-btn-ghost" data-testid="analyze-from-style-cta">
              Try on this style <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>

          <div className="border-t border-bone/10 pt-8">
            <p className="font-overline mb-3">AI grooming tips</p>
            {tips ? (
              <pre className="font-sans whitespace-pre-wrap text-bone leading-relaxed text-sm" data-testid="grooming-tips-content">{tips}</pre>
            ) : (
              <button onClick={loadTips} disabled={loadingTips} className="crown-btn-ghost" data-testid="load-tips-btn">
                {loadingTips ? (<><Loader2 className="animate-spin" size={14} strokeWidth={1.5} /> Consulting</>) : ("Get personalised tips")}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
